import { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import DashboardLayout from "../components/DashboardLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { User, Mail, Crown, Globe, AlertTriangle, Trash2, RefreshCw } from "lucide-react";
import { useLanguage } from "../context/LanguageContext";
import { API } from "../App";

const AccountSettings = ({ auth }) => {
  const { t, language, setLanguage } = useLanguage();
  const navigate = useNavigate();
  const [showResetDialog, setShowResetDialog] = useState(false);
  const [resetConfirmText, setResetConfirmText] = useState("");
  const [isResetting, setIsResetting] = useState(false);
  const [isUpdatingLanguage, setIsUpdatingLanguage] = useState(false);

  const tierInfo = {
    free: { label: "Free", color: "bg-slate-100 text-slate-700", icon: null },
    pro: { label: "Pro", color: "bg-blue-100 text-blue-700", icon: Crown },
    premium: { label: "Premium", color: "bg-yellow-100 text-yellow-700", icon: Crown }
  };

  const currentTier = tierInfo[auth.user?.subscription_tier || "free"];

  const handleLanguageChange = async (newLanguage) => {
    setIsUpdatingLanguage(true);
    try {
      await axios.put(
        `${API}/user/language`,
        { language: newLanguage },
        {
          headers: auth.token ? { Authorization: `Bearer ${auth.token}` } : {},
          withCredentials: true
        }
      );
      
      setLanguage(newLanguage);
      auth.setUser({ ...auth.user, language: newLanguage });
      toast.success(newLanguage === "fr" ? "Langue mise à jour!" : "Language updated!");
    } catch (error) {
      toast.error("Failed to update language");
    } finally {
      setIsUpdatingLanguage(false);
    }
  };

  const handleResetProgress = async () => {
    if (resetConfirmText !== "RESET") {
      toast.error(t("account.typeToConfirm") || "Type RESET to confirm");
      return;
    }

    setIsResetting(true);
    try {
      await axios.post(
        `${API}/user/reset-progress`,
        {},
        {
          headers: auth.token ? { Authorization: `Bearer ${auth.token}` } : {},
          withCredentials: true
        }
      );
      
      toast.success(t("account.resetSuccess") || "Progress reset successfully!");
      setShowResetDialog(false);
      setResetConfirmText("");
      
      // Redirect to dashboard after brief delay
      setTimeout(() => {
        navigate("/dashboard");
      }, 1500);
    } catch (error) {
      toast.error("Failed to reset progress. Please try again.");
    } finally {
      setIsResetting(false);
    }
  };

  return (
    <DashboardLayout auth={auth}>
      <div className="max-w-3xl mx-auto space-y-6">
        {/* Header */}
        <div>
          <h1 className="font-heading font-bold text-2xl md:text-3xl text-slate-900">
            {t("account.title") || "Account Settings"}
          </h1>
          <p className="text-slate-600 mt-1">
            {language === "fr" 
              ? "Gérez vos paramètres de compte et préférences"
              : "Manage your account settings and preferences"
            }
          </p>
        </div>

        {/* Personal Information */}
        <Card data-testid="personal-info-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <User className="w-5 h-5 text-slate-500" />
              {t("account.personalInfo") || "Personal Information"}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <Label className="text-slate-600">{t("account.name") || "Name"}</Label>
                <div className="mt-1 flex items-center gap-2 p-3 bg-slate-50 rounded-lg">
                  <User className="w-4 h-4 text-slate-400" />
                  <span className="text-slate-900">{auth.user?.name || "User"}</span>
                </div>
              </div>
              <div>
                <Label className="text-slate-600">{t("account.email") || "Email"}</Label>
                <div className="mt-1 flex items-center gap-2 p-3 bg-slate-50 rounded-lg">
                  <Mail className="w-4 h-4 text-slate-400" />
                  <span className="text-slate-900">{auth.user?.email}</span>
                </div>
              </div>
            </div>
            <div>
              <Label className="text-slate-600">{t("account.subscription") || "Subscription"}</Label>
              <div className="mt-1 flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                <Badge className={currentTier.color}>
                  {currentTier.icon && <Crown className="w-3 h-3 mr-1" />}
                  {currentTier.label}
                </Badge>
                {auth.user?.subscription_tier === "free" && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => navigate("/pricing")}
                    className="ml-auto"
                  >
                    {t("common.upgrade") || "Upgrade"}
                  </Button>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Language Preference */}
        <Card data-testid="language-settings-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Globe className="w-5 h-5 text-slate-500" />
              {t("account.languagePreference") || "Language Preference"}
            </CardTitle>
            <CardDescription>
              {t("account.languageDescription") || "Choose your preferred language for the application"}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Select
              value={auth.user?.language || "en"}
              onValueChange={handleLanguageChange}
              disabled={isUpdatingLanguage}
            >
              <SelectTrigger className="w-full sm:w-64" data-testid="language-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="en">
                  <div className="flex items-center gap-2">
                    <span>🇬🇧</span>
                    <span>English</span>
                  </div>
                </SelectItem>
                <SelectItem value="fr">
                  <div className="flex items-center gap-2">
                    <span>🇫🇷</span>
                    <span>Français</span>
                  </div>
                </SelectItem>
              </SelectContent>
            </Select>
          </CardContent>
        </Card>

        {/* Danger Zone - Reset Progress */}
        <Card className="border-red-200" data-testid="danger-zone-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="w-5 h-5" />
              {t("account.dangerZone") || "Danger Zone"}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="p-4 bg-red-50 rounded-lg border border-red-100">
              <h3 className="font-semibold text-slate-900 flex items-center gap-2">
                <RefreshCw className="w-4 h-4" />
                {t("account.resetProgress") || "Reset All Progress"}
              </h3>
              <p className="text-sm text-slate-600 mt-2 mb-4">
                {t("account.resetDescription") || 
                  "This will permanently delete all your test history, scores, notes, journey progress, and study streaks. Your account will remain active."
                }
              </p>
              <Button
                variant="destructive"
                onClick={() => setShowResetDialog(true)}
                data-testid="reset-progress-btn"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                {t("account.resetButton") || "Reset My Progress"}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Reset Confirmation Dialog */}
        <Dialog open={showResetDialog} onOpenChange={setShowResetDialog}>
          <DialogContent data-testid="reset-confirm-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-red-600">
                <AlertTriangle className="w-5 h-5" />
                {t("account.resetConfirmTitle") || "Are you absolutely sure?"}
              </DialogTitle>
              <DialogDescription className="text-slate-600 pt-2">
                {t("account.resetConfirmDescription") || 
                  "This action cannot be undone. All your progress data will be permanently deleted."
                }
              </DialogDescription>
            </DialogHeader>
            
            <div className="py-4">
              <Label className="text-sm text-slate-700">
                {t("account.typeToConfirm") || "Type RESET to confirm"}
              </Label>
              <Input
                value={resetConfirmText}
                onChange={(e) => setResetConfirmText(e.target.value.toUpperCase())}
                placeholder="RESET"
                className="mt-2"
                data-testid="reset-confirm-input"
              />
            </div>

            <DialogFooter className="gap-2 sm:gap-0">
              <Button
                variant="outline"
                onClick={() => {
                  setShowResetDialog(false);
                  setResetConfirmText("");
                }}
              >
                {t("common.cancel") || "Cancel"}
              </Button>
              <Button
                variant="destructive"
                onClick={handleResetProgress}
                disabled={resetConfirmText !== "RESET" || isResetting}
                data-testid="confirm-reset-btn"
              >
                {isResetting ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    {t("account.resetting") || "Resetting..."}
                  </>
                ) : (
                  <>
                    <Trash2 className="w-4 h-4 mr-2" />
                    {t("account.resetButton") || "Reset My Progress"}
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default AccountSettings;
